import { type PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { Options, RolePermissions } from "~/models/interface/common.interface";
import type { UserBalanceDetails, UserData } from "~/models/interface/user.interface";
import type { RootState } from "./index";

import { DEFAULT_TIMEZONE } from "~/utils/date-utils";

export interface GlobalState {
  deviceFingerPrint: string;
  tzName: string;
  otpModal: boolean;
  quickPhoneSearchModal: boolean;
  quickIpSearchModal: boolean;
  isAllowimpersonated: boolean;
  operatorOptions: Options[];
  userData: UserData | null;
  permissions: RolePermissions | null;
  userBalanceDetails: UserBalanceDetails | null;
  mobileView: boolean;
  sidebarOpen: boolean;
  checkQueue: boolean;
  exportReady: boolean;
  bulkBonusRefetchList: boolean;
  stepIndex: number;
  isDriverRunning: boolean;
  exportStack: { name: string; url: string; description?: string }[];
  toastDescription: string | null;
  selectedRowId: string | null;
  runFeedsFetcher: boolean;
  hideBoRoleUnavailablePermissions: boolean;
  hideOperatorRoleUnavailablePermissions: boolean;
  pgsSyncJob: { jobId: string; total: number } | null;
  pgsSyncComplete: boolean;
}

const initialState: GlobalState = {
  deviceFingerPrint: "",
  tzName: DEFAULT_TIMEZONE,
  otpModal: false,
  quickPhoneSearchModal: false,
  quickIpSearchModal: false,
  isAllowimpersonated: false,
  operatorOptions: [],
  userData: null,
  permissions: null,
  userBalanceDetails: null,
  mobileView: false,
  sidebarOpen: false,
  checkQueue: false,
  exportReady: false,
  exportStack: [],
  bulkBonusRefetchList: false,
  stepIndex: 0,
  isDriverRunning: false,
  toastDescription: null,
  selectedRowId: null,
  runFeedsFetcher: false,
  hideBoRoleUnavailablePermissions: true,
  hideOperatorRoleUnavailablePermissions: false,
  pgsSyncJob: null,
  pgsSyncComplete: false,
};

export const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    // handle dark mode
    setDeviceFingerPrint: (state: RootState, action: PayloadAction<string>) => {
      state.deviceFingerPrint = action.payload;
    },
    setTzName: (state: RootState, action: PayloadAction<string>) => {
      state.tzName = action.payload;
    },
    setOtpModal: (state: RootState, action: PayloadAction<boolean>) => {
      state.otpModal = action.payload;
    },
    setIsAllowImpersonated: (state: RootState, action: PayloadAction<boolean>) => {
      state.isAllowimpersonated = action.payload;
    },
    setOperatorOptions: (state: RootState, action: PayloadAction<Options[]>) => {
      state.operatorOptions = action.payload;
    },
    setUserDetails: (state: RootState, action: PayloadAction<{ userData: UserData; permissions: RolePermissions } | null>) => {
      state.userData = action.payload?.userData;
      state.permissions = action.payload?.permissions;
    },
    setUserBalanceDetails: (state: RootState, action: PayloadAction<UserBalanceDetails>) => {
      state.userBalanceDetails = action.payload;
    },
    setQuickPhoneSearchModal: (state: RootState, action: PayloadAction<boolean>) => {
      state.quickPhoneSearchModal = action.payload;
    },
    setQuickIpSearchModal: (state: RootState, action: PayloadAction<boolean>) => {
      state.quickIpSearchModal = action.payload;
    },
    setMobileView: (state: RootState, action: PayloadAction<boolean>) => {
      state.mobileView = action.payload;
    },
    setSidebarOpen: (state: RootState, action: PayloadAction<boolean>) => {
      state.sidebarOpen = action.payload;
    },
    setCheckQueue: (state: RootState, action: PayloadAction<boolean>) => {
      state.checkQueue = action.payload;
    },
    setExportReady: (state: RootState, action: PayloadAction<boolean>) => {
      state.exportReady = action.payload;
    },
    setExportStack: (state: RootState, action: PayloadAction<{ name: string; url: string }[]>) => {
      state.exportStack = action.payload;
    },
    setBulkBonusRefetchList: (state: RootState, action: PayloadAction<boolean>) => {
      state.bulkBonusRefetchList = action.payload;
    },
    setStepIndex: (state: RootState, action: PayloadAction<number>) => {
      state.stepIndex = action.payload;
    },
    setIsDriverRunning: (state: RootState, action: PayloadAction<boolean>) => {
      state.isDriverRunning = action.payload;
    },
    setToastDescription: (state: RootState, action: PayloadAction<string | null>) => {
      state.toastDescription = action.payload;
    },
    setSelectedRowId: (state: RootState, action: PayloadAction<string | null>) => {
      state.selectedRowId = action.payload;
    },
    setRunFeedsFetcher: (state: RootState, action: PayloadAction<boolean>) => {
      state.runFeedsFetcher = action.payload;
    },
    setHideOperatorRoleUnavailablePermissions: (state: RootState, action: PayloadAction<boolean>) => {
      state.hideOperatorRoleUnavailablePermissions = action.payload;
    },
    setHideBetstudioRoleUnavailablePermissions: (state: RootState, action: PayloadAction<boolean>) => {
      state.hideBoRoleUnavailablePermissions = action.payload;
    },
    setPgsSyncJob: (state: RootState, action: PayloadAction<{ jobId: string; total: number } | null>) => {
      state.pgsSyncJob = action.payload;
    },
    setPgsSyncComplete: (state: RootState, action: PayloadAction<boolean>) => {
      state.pgsSyncComplete = action.payload;
    },
  },
});

export const {
  setDeviceFingerPrint,
  setTzName,
  setOtpModal,
  setIsAllowImpersonated,
  setOperatorOptions,
  setUserDetails,
  setUserBalanceDetails,
  setQuickPhoneSearchModal,
  setQuickIpSearchModal,
  setMobileView,
  setSidebarOpen,
  setCheckQueue,
  setExportReady,
  setExportStack,
  setBulkBonusRefetchList,
  setStepIndex,
  setIsDriverRunning,
  setToastDescription,
  setSelectedRowId,
  setRunFeedsFetcher,
  setHideOperatorRoleUnavailablePermissions,
  setHideBetstudioRoleUnavailablePermissions,
  setPgsSyncJob,
  setPgsSyncComplete,
} = globalSlice.actions;

export default globalSlice.reducer;
